import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Alert, Button, DatePicker, Drawer, Modal, Space, Tree, message } from "antd";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { clearSprZalGraf, fillSprZalGraf } from "./spr-zal-api";
import "./spr-zal-fill-modal.css";

dayjs.extend(isoWeek);

const DRAWER_WIDTH_KEY = "planOnline.sprZal.fillDrawer.width";
const MIN_WIDTH = 480;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 560;

const clampWidth = (value) => {
  const max = typeof window === "undefined" ? MAX_WIDTH : Math.min(MAX_WIDTH, window.innerWidth);
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const defaultPeriod = () => [dayjs().startOf("month"), dayjs().endOf("month")];

/** Понедельник шаблонной недели: неделя начала периода минус 7 дней (как Delphi). */
const defaultWeekStart = (periodBeg) => {
  const beg = periodBeg?.isValid?.() ? periodBeg : dayjs();
  return beg.startOf("isoWeek").subtract(7, "day");
};

const formatWeekLabel = (weekStart) => {
  const end = weekStart.endOf("isoWeek");
  return `${weekStart.format("DD.MM.YY")} — ${end.format("DD.MM.YY")}`;
};

const findNode = (nodes, id) => {
  for (const n of nodes || []) {
    if (n.idZal === id) return n;
    const nested = findNode(n.children, id);
    if (nested) return nested;
  }
  return null;
};

/** Дерево Ant Design: группы можно выбирать, в fill/clear уходят только кабинеты (листы). */
const toCheckTree = (nodes) =>
  (nodes || []).map((n) => {
    const isGroup = Boolean(n.isGroup);
    const children = n.children?.length ? toCheckTree(n.children) : undefined;
    return {
      key: String(n.idZal),
      title: n.name || `№${n.idZal}`,
      idZal: n.idZal,
      isGroup,
      children
    };
  });

const collectLeafKeys = (nodes, acc = []) => {
  (nodes || []).forEach((n) => {
    if (!n.isGroup) acc.push(n.key);
    if (n.children?.length) collectLeafKeys(n.children, acc);
  });
  return acc;
};

/** Ключи от корня до узла включительно (для раскрытия пути к группе). */
const findPathKeys = (nodes, targetId, path = []) => {
  for (const n of nodes || []) {
    const next = [...path, String(n.idZal)];
    if (n.idZal === targetId) return next;
    const found = findPathKeys(n.children, targetId, next);
    if (found) return found;
  }
  return null;
};

/**
 * Заполнение/очистка графиков (Delphi TFrmSprZalFill) — drawer как у карточки кабинета.
 */
const SprZalFillModal = ({ open, contextIdZal, treeData, onClose, onDone }) => {
  const [size, setSize] = useState(loadWidth);
  const [period, setPeriod] = useState(defaultPeriod);
  const [weekStart, setWeekStart] = useState(() => defaultWeekStart(dayjs().startOf("month")));
  const [scopeTree, setScopeTree] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const applyScope = useCallback((nodes, { checkedLeaves, expandGroupId } = {}) => {
    const tree = toCheckTree(nodes);
    setScopeTree(tree);
    setCheckedKeys(checkedLeaves ?? collectLeafKeys(tree));
    // Корневые узлы; если открыли с группы — ещё её (и путь к ней).
    const expanded = tree.filter((n) => n.children?.length).map((n) => n.key);
    if (expandGroupId != null) {
      const path = findPathKeys(nodes, expandGroupId) || [];
      path.forEach((key) => {
        if (!expanded.includes(key)) expanded.push(key);
      });
    }
    setExpandedKeys(expanded);
  }, []);

  const scopeFromContext = useCallback(
    (id) => {
      const node = findNode(treeData, id);
      if (!node) {
        applyScope(treeData);
        return;
      }
      if (node.isGroup) {
        applyScope(treeData, {
          checkedLeaves: collectLeafKeys(toCheckTree([node])),
          expandGroupId: node.idZal
        });
        return;
      }
      applyScope(treeData, { checkedLeaves: [String(node.idZal)] });
    },
    [treeData, applyScope]
  );

  useEffect(() => {
    if (!open) return;
    const [beg, end] = defaultPeriod();
    setPeriod([beg, end]);
    setWeekStart(defaultWeekStart(beg));
    setError("");
    if (contextIdZal != null) scopeFromContext(contextIdZal);
    else applyScope(treeData);
  }, [open, contextIdZal, treeData, scopeFromContext, applyScope]);

  const leafKeySet = useMemo(() => new Set(collectLeafKeys(scopeTree)), [scopeTree]);

  const checkedIds = useMemo(
    () =>
      checkedKeys
        .filter((k) => leafKeySet.has(String(k)))
        .map((k) => toNumber(k))
        .filter((id) => id != null),
    [checkedKeys, leafKeySet]
  );

  const onCheck = (keys) => {
    const raw = Array.isArray(keys) ? keys : keys?.checked || [];
    // В UI сохраняем и группы (для выбора ветки); в API — только листы (checkedIds).
    setCheckedKeys(raw.map(String));
  };

  const setAllChecked = (checked) => {
    setCheckedKeys(checked ? collectLeafKeys(scopeTree) : []);
  };

  const uncheckOk = (items) => {
    const okSet = new Set(
      (items || [])
        .filter((i) => pick(i, "ok", "Ok") === true || pick(i, "ok", "Ok") === 1)
        .map((i) => String(toNumber(pick(i, "idZal", "IdZal"))))
        .filter((id) => id !== "null")
    );
    setCheckedKeys((prev) => prev.filter((k) => !okSet.has(String(k))));
  };

  const periodPayload = () => {
    const [beg, end] = period || [];
    if (!beg?.isValid?.() || !end?.isValid?.()) {
      throw new Error("Не правильно задан интервал дат");
    }
    if (end.isBefore(beg, "day")) {
      throw new Error("Не правильно задан интервал дат");
    }
    if (!checkedIds.length) {
      throw new Error("Укажите кабинеты");
    }
    return {
      idZals: checkedIds,
      targetBeg: beg.format("YYYY-MM-DD"),
      targetEnd: end.format("YYYY-MM-DD")
    };
  };

  const onFill = async () => {
    setError("");
    let body;
    try {
      body = {
        ...periodPayload(),
        masterBeg: weekStart.format("YYYY-MM-DD"),
        masterEnd: weekStart.endOf("isoWeek").format("YYYY-MM-DD")
      };
    } catch (e) {
      setError(e.message);
      return;
    }

    setBusy(true);
    try {
      const data = await fillSprZalGraf(body);
      const items = pick(data, "items", "Items") || [];
      uncheckOk(items);
      const allOk = items.length > 0 && items.every((i) => pick(i, "ok", "Ok"));
      if (allOk) {
        message.success("Заполнены все графики выбранных кабинетов");
      } else {
        message.error(
          "Не все графики выбранных кабинетов были заполнены, т.к. есть сеансы в периоде заполнения"
        );
      }
      onDone?.();
    } catch (e) {
      setError(e.message || "Не удалось заполнить");
    } finally {
      setBusy(false);
    }
  };

  const onClear = () => {
    setError("");
    let body;
    try {
      body = periodPayload();
    } catch (e) {
      setError(e.message);
      return;
    }

    Modal.confirm({
      title: "Очистить графики кабинетов?",
      okText: "Очистить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusy(true);
        try {
          const data = await clearSprZalGraf(body);
          const items = pick(data, "items", "Items") || [];
          uncheckOk(items);
          const allOk = items.length > 0 && items.every((i) => pick(i, "ok", "Ok"));
          if (allOk) {
            message.success("Очищены все графики выбранных кабинетов");
          } else {
            message.error(
              "Не все графики выбранных кабинетов были очищены, т.к. есть записи клиентов на сеансы в периоде очистки"
            );
          }
          onDone?.();
        } catch (e) {
          setError(e.message || "Не удалось очистить");
          return Promise.reject(e);
        } finally {
          setBusy(false);
        }
      }
    });
  };

  return (
    <Drawer
      placement="right"
      title="Заполнение графиков сеансов по кабинетам"
      size={size}
      maxSize={MAX_WIDTH}
      open={open}
      onClose={onClose}
      destroyOnClose
      closable={{ placement: "end" }}
      rootClassName="spr-zal-fill-drawer"
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      <div className="spr-zal-fill">
        <div className="spr-zal-fill__body">
          {error ? <Alert className="spr-zal-fill__error" type="error" showIcon message={error} /> : null}

          <Space wrap size={[8, 8]} className="spr-zal-fill__list-actions">
            <Button className="spr-zal-fill__btn" onClick={() => setAllChecked(true)} disabled={busy}>
              Выбрать все
            </Button>
            <Button className="spr-zal-fill__btn" onClick={() => setAllChecked(false)} disabled={busy}>
              Снять все
            </Button>
          </Space>

          <div className="spr-zal-fill__tree">
            {scopeTree.length ? (
              <Tree
                checkable
                selectable={false}
                disabled={busy}
                treeData={scopeTree}
                checkedKeys={checkedKeys}
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                onCheck={onCheck}
              />
            ) : (
              <div className="spr-zal-fill__empty">Нет кабинетов</div>
            )}
          </div>

          <div className="spr-zal-fill__section spr-zal-fill__section--fixed">
            <div className="spr-zal-fill__label">Неделя-шаблон</div>
            <Space>
              <Button
                icon={<LeftOutlined />}
                size="small"
                disabled={busy}
                onClick={() => setWeekStart((w) => w.subtract(7, "day"))}
              />
              <span className="spr-zal-fill__week">{formatWeekLabel(weekStart)}</span>
              <Button
                icon={<RightOutlined />}
                size="small"
                disabled={busy}
                onClick={() => setWeekStart((w) => w.add(7, "day"))}
              />
            </Space>
          </div>

          <div className="spr-zal-fill__section spr-zal-fill__section--fixed">
            <div className="spr-zal-fill__label">Период заполнения / очистки</div>
            <DatePicker.RangePicker
              value={period}
              onChange={(v) => setPeriod(v)}
              format="DD.MM.YYYY"
              allowClear={false}
              disabled={busy}
              style={{ width: "100%" }}
            />
          </div>
        </div>

        <div className="spr-zal-fill__footer">
          <Button
            className="spr-zal-fill__btn"
            onClick={onFill}
            type="primary"
            loading={busy}
            disabled={busy}
          >
            Заполнить
          </Button>
          <Button className="spr-zal-fill__btn" onClick={onClear} danger disabled={busy} loading={busy}>
            Очистить
          </Button>
          <Button className="spr-zal-fill__btn" onClick={onClose} disabled={busy}>
            Закрыть
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default SprZalFillModal;
